'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import TierAvatar from '@/components/TierAvatar';
import { TierName } from '@/lib/tierConfig';
import { getWinningVideos, WinningVideo } from '@/lib/winningBountiesStore';
import { ChevronDown, ChevronUp, Play, Check } from 'lucide-react';
import BrandLogo from '@/components/ui/BrandLogo';
import { createClient } from '@/lib/supabase/client';

// ─── Design tokens ────────────────────────────────────────────────────────────
const BG = '#0A0E0C';
const SURFACE = '#131A16';
const BORDER = 'rgba(255,255,255,0.08)';
const DIVIDER = '1px solid rgba(255,255,255,0.07)';
const EMERALD = '#10B981';
const EMERALD_TEXT = '#06281C';
const TEXT_PRIMARY = '#FFFFFF';
const TEXT_SECONDARY = '#9CA3AF';
const TEXT_MUTED = '#6B7280';
const TEXT_BODY = '#D1D5DB';
const CALLOUT_BG = '#0F1F18';
const CALLOUT_BORDER = 'rgba(16,185,129,0.35)';
const CALLOUT_TEXT = '#A7F3D0';
const INACTIVE_BAR = '#374151';
const EMERALD_PILL_BG = 'rgba(16,185,129,0.15)';

// ─── Tier ladder data ─────────────────────────────────────────────────────────
const TIER_LADDER = [
{ name: 'Bronze', barPct: 5, fill: INACTIVE_BAR, value: 'Start · $20/bounty', valueColor: TEXT_MUTED, isLegend: false, isDiamond: false },
{ name: 'Silver', barPct: 10, fill: INACTIVE_BAR, value: '$20 · $25/bounty', valueColor: TEXT_BODY, isLegend: false, isDiamond: false },
{ name: 'Gold', barPct: 16, fill: INACTIVE_BAR, value: '$70 · $30/bounty', valueColor: TEXT_BODY, isLegend: false, isDiamond: false },
{ name: 'Platinum', barPct: 23, fill: INACTIVE_BAR, value: '$160 · $35/bounty', valueColor: TEXT_BODY, isLegend: false, isDiamond: false },
{ name: 'Diamond', barPct: 31, fill: 'rgba(16,185,129,0.45)', value: '$300 · $40/bounty', valueColor: TEXT_BODY, isLegend: false, isDiamond: true },
{ name: 'Elite', barPct: 40, fill: 'rgba(16,185,129,0.55)', value: '$500 · $50/bounty', valueColor: TEXT_BODY, isLegend: false, isDiamond: false },
{ name: 'Master', barPct: 50, fill: 'rgba(16,185,129,0.65)', value: '$800 · $60/bounty', valueColor: TEXT_BODY, isLegend: false, isDiamond: false },
{ name: 'Grandmaster', barPct: 61, fill: 'rgba(16,185,129,0.75)', value: '$1,220 · $70/bounty', valueColor: TEXT_BODY, isLegend: false, isDiamond: false },
{ name: 'Mythic', barPct: 73, fill: 'rgba(16,185,129,0.85)', value: '$1,780 · $80/bounty', valueColor: TEXT_BODY, isLegend: false, isDiamond: false },
{ name: 'Legend', barPct: 86, fill: EMERALD, value: '$2,500+ · $100/bounty', valueColor: EMERALD, isLegend: true, isDiamond: false }];


// ─── FAQ data ─────────────────────────────────────────────────────────────────
const FAQ_ITEMS = [
{ q: 'Does it cost anything to join?', a: 'No. Joining is free, staying is free, and it will never cost anything. We pay you.' },
{ q: 'How much does each bounty pay?', a: 'Every bounty pays your current level rate, from $20 at Level 1 up to $100 at Level 10. The amount shown on each bounty is what you\'ll earn at your level.' },
{ q: 'Do I have to take the full-time job?', a: 'No. The full-time role is an optional bonus for top performers, not a requirement. Most members simply keep winning bounties and getting paid for as long as they like.' },
{ q: 'Do I need a resume or experience?', a: 'No resume and no interviews to start earning. Pass a short quiz, then your work speaks.' },
{ q: 'How do I get paid?', a: 'Through Wise, in USD, on the 25th of every month.' },
{ q: 'What if my submission is rejected?', a: 'You get revision notes and up to 3 chances to fix it. Approved work always gets paid.' },
{ q: 'How many bounties can I take?', a: 'Three at a time, unlimited per month.' },
{ q: 'What AI tools do I need?', a: 'Any tools you like. We judge the output, not the software.' }];


// ─── Static leaderboard data (FIX 6) ─────────────────────────────────────────
const STATIC_LEADERBOARD = [
{ rank: 1, username: 'kylo_ai', level: 9, tier: 'Legend' as TierName, earnings: '$2,140' },
{ rank: 2, username: 'mariav.creates', level: 7, tier: 'Master' as TierName, earnings: '$1,480' },
{ rank: 3, username: 'jp_prompts', level: 6, tier: 'Elite' as TierName, earnings: '$960' }];


// ─── Logo with text fallback (FIX 8) ─────────────────────────────────────────
function LogoWithFallback({ height, className = '' }: {height: number;className?: string;}) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <span style={{ fontSize: Math.round(height * 0.6), fontWeight: 700, lineHeight: 1 }}>
        <span style={{ color: TEXT_PRIMARY }}>Win</span>
        <span style={{ color: EMERALD }}>Bounty</span>
      </span>);

  }
  return (
    <BrandLogo
      variant="light"
      size={height}
      className={className} />);


}

// ─── Tier bar row with scroll-triggered animation ─────────────────────────────
function TierBarRow({
  tier,
  sectionRef



}: {tier: (typeof TIER_LADDER)[0];sectionRef: React.RefObject<HTMLDivElement | null>;}) {
  const [width, setWidth] = useState(0);
  const animated = useRef(false);

  useEffect(() => {
    if (animated.current) return;
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !animated.current) {
          animated.current = true;
          requestAnimationFrame(() => setWidth(tier.barPct));
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [tier.barPct, sectionRef]);

  return (
    <div className="flex items-center w-full" style={{ gap: 0 }}>
      {/* Tier name — fixed-width LEFT-aligned column, 74px mobile */}
      <div
        className="flex-shrink-0"
        style={{ width: 74, minWidth: 74 }}>

        <span
          style={{
            color: tier.isLegend ? TEXT_PRIMARY : tier.isDiamond ? EMERALD : TEXT_SECONDARY,
            fontWeight: tier.isLegend ? 600 : 400,
            fontSize: 10.5,
            display: 'block',
            lineHeight: 1.3
          }}>

          {tier.name}
        </span>
        {tier.isDiamond &&
        <span style={{ fontSize: 9, color: EMERALD, display: 'block', lineHeight: 1.3 }}>avg. hire</span>
        }
      </div>

      {/* Bar track — invisible, fills remaining width (no fixed px on mobile) */}
      <div
        className="flex-shrink-0"
        style={{
          flex: '1 0 0',
          minWidth: 0,
          backgroundColor: 'transparent',
          borderRadius: 3,
          height: 9,
          overflow: 'visible',
          position: 'relative'
        }}>

        <div
          style={{
            width: `${width}%`,
            height: '100%',
            backgroundColor: tier.fill,
            borderRadius: 3,
            transition: 'width 600ms ease-out'
          }} />

      </div>

      {/* Value — outside track, fixed width, right of bar */}
      <div className="flex-shrink-0 flex items-center" style={{ paddingLeft: 8, gap: 6 }}>
        <span
          className="tabular-nums"
          style={{
            color: tier.valueColor,
            fontWeight: tier.isLegend ? 600 : 400,
            fontSize: 11.5
          }}>

          {tier.value}
        </span>
      </div>
    </div>);

}

// ─── Desktop tier bar row (110px label, 480px fixed track, 12px bars) ────────
function TierBarRowDesktop({
  tier,
  sectionRef



}: {tier: (typeof TIER_LADDER)[0];sectionRef: React.RefObject<HTMLDivElement | null>;}) {
  const [width, setWidth] = useState(0);
  const animated = useRef(false);

  useEffect(() => {
    if (animated.current) return;
    const el = sectionRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !animated.current) {
          animated.current = true;
          requestAnimationFrame(() => setWidth(tier.barPct));
          observer.disconnect();
        }
      },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [tier.barPct, sectionRef]);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
      {/* 110px left-aligned label column */}
      <div style={{ width: 110, minWidth: 110, flexShrink: 0 }}>
        <span
          style={{
            color: tier.isLegend ? TEXT_PRIMARY : tier.isDiamond ? EMERALD : TEXT_SECONDARY,
            fontWeight: tier.isLegend ? 600 : 400,
            fontSize: 13,
            display: 'block',
            lineHeight: 1.3
          }}>

          {tier.name}
        </span>
        {tier.isDiamond &&
        <span style={{ fontSize: 10, color: EMERALD, display: 'block', lineHeight: 1.3 }}>avg. hire</span>
        }
      </div>

      {/* 480px invisible fixed track — no background, no border */}
      <div
        style={{
          width: 480,
          minWidth: 480,
          flexShrink: 0,
          backgroundColor: 'transparent',
          borderRadius: 3,
          height: 12,
          overflow: 'visible',
          position: 'relative'
        }}>

        <div
          style={{
            width: `${width}%`,
            height: '100%',
            backgroundColor: tier.fill,
            borderRadius: 3,
            transition: 'width 600ms ease-out'
          }} />

      </div>

      {/* Value — 12px to the right of track end */}
      <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', paddingLeft: 12, gap: 8 }}>
        <span
          className="tabular-nums"
          style={{
            color: tier.valueColor,
            fontWeight: tier.isLegend ? 600 : 400,
            fontSize: 13
          }}>

          {tier.value}
        </span>
        {tier.isDiamond &&
        <span
          style={{
            backgroundColor: EMERALD_PILL_BG,
            color: EMERALD,
            fontSize: 11,
            borderRadius: 6,
            padding: '2px 8px',
            whiteSpace: 'nowrap'
          }}>

            Average hire point
          </span>
        }
      </div>
    </div>);

}

// ─── Video card ───────────────────────────────────────────────────────────────
function VideoCard({
  video,
  isPlaying,
  onPlay,
  onPause





}: {video: WinningVideo;isPlaying: boolean;onPlay: (id: string) => void;onPause: (id: string) => void;}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [showOverlay, setShowOverlay] = useState(true);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (isPlaying) {
      el.play().catch(() => {});
      setShowOverlay(false);
    } else {
      el.pause();
      setShowOverlay(true);
    }
  }, [isPlaying]);

  const handleClick = () => {
    if (isPlaying) onPause(video.id);else
    onPlay(video.id);
  };

  return (
    <div
      style={{
        backgroundColor: SURFACE,
        border: `1px solid ${BORDER}`,
        borderRadius: 8,
        overflow: 'hidden'
      }}>

      <div className="relative w-full" style={{ paddingBottom: '177.78%' }}>
        <div className="absolute inset-0 bg-black">
          <video
            ref={videoRef}
            src={video.videoUrl}
            poster={video.thumbnailUrl || undefined}
            loop
            muted
            playsInline
            preload="metadata"
            className="w-full h-full object-cover"
            onPlay={() => onPlay(video.id)}
            onPause={() => onPause(video.id)} />

          {showOverlay &&
          <div
            className="absolute inset-0 flex items-center justify-center cursor-pointer"
            style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}
            onClick={handleClick}>

              {video.thumbnailUrl &&
            <img
              src={video.thumbnailUrl}
              alt={`Thumbnail for ${video.title}`}
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy" />

            }
              <div className="relative z-10 w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-xl">
                <Play size={22} className="ml-1" style={{ color: '#000' }} fill="#000" />
              </div>
            </div>
          }
          {!showOverlay &&
          <div className="absolute inset-0 cursor-pointer" onClick={handleClick} />
          }
        </div>
      </div>
    </div>);

}

// ─── Homepage featured video card (9:16 aspect, dark, play icon) ─────────────
interface HomepageFeaturedVideo {
  id: string;
  position: number;
  title: string | null;
  video_url: string;
  thumbnail_url: string | null;
  is_active: boolean;
}

function HomepageFeaturedVideoCard({ video }: {video: HomepageFeaturedVideo;}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);

  const handleClick = () => {
    const el = videoRef.current;
    if (!el) return;
    if (isPlaying) {
      el.pause();
      setIsPlaying(false);
      setShowOverlay(true);
    } else {
      el.play().catch(() => {});
      setIsPlaying(true);
      setShowOverlay(false);
    }
  };

  return (
    <div
      style={{
        backgroundColor: '#131A16',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 10,
        overflow: 'hidden',
        cursor: 'pointer'
      }}
      onClick={handleClick}>

      <div className="relative w-full" style={{ paddingBottom: '177.78%' }}>
        <div className="absolute inset-0">
          {video.video_url &&
          <video
            ref={videoRef}
            src={video.video_url}
            poster={video.thumbnail_url || undefined}
            loop
            muted
            playsInline
            preload="metadata"
            className="w-full h-full object-cover" />

          }
          {showOverlay &&
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ backgroundColor: video.thumbnail_url ? 'rgba(0,0,0,0.3)' : 'transparent' }}>

              {video.thumbnail_url &&
            <img
              src={video.thumbnail_url}
              alt={video.title || 'Featured winning video'}
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy" />

            }
              <div className="relative z-10 flex items-center justify-center">
                <Play
                size={28}
                style={{ color: '#10B981' }}
                fill="none"
                strokeWidth={2} />

              </div>
            </div>
          }
        </div>
      </div>
    </div>);

}

// Placeholder card when no video is set
function HomepageFeaturedVideoPlaceholder() {
  return (
    <div
      style={{
        backgroundColor: '#131A16',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 10,
        overflow: 'hidden'
      }}>

      <div className="relative w-full" style={{ paddingBottom: '177.78%' }}>
        <div className="absolute inset-0 flex items-center justify-center">
          <Play
            size={28}
            style={{ color: '#10B981' }}
            fill="none"
            strokeWidth={2} />

        </div>
      </div>
    </div>);

}

// ─── Hero phone visual (desktop only) ────────────────────────────────────────
function HeroVisual({
  videos,
  playingId,
  onPlay,
  onPause





}: {videos: WinningVideo[];playingId: string | null;onPlay: (id: string) => void;onPause: (id: string) => void;}) {
  const heroVideo = videos[0];
  const videoRef = useRef<HTMLVideoElement>(null);
  const isPlaying = heroVideo ? playingId === heroVideo.id : false;

  // Auto-play first video muted+looping when it becomes available (lazy)
  useEffect(() => {
    const el = videoRef.current;
    if (!el || !heroVideo) return;
    el.play().catch(() => {});
    onPlay(heroVideo.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heroVideo?.id]);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !heroVideo) return;
    if (isPlaying) el.play().catch(() => {});else
    el.pause();
  }, [isPlaying, heroVideo]);

  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      {/* ── Volume buttons (left side) ── */}
      <div style={{
        position: 'absolute',
        left: -5,
        top: 90,
        width: 4,
        height: 28,
        backgroundColor: '#4a3f5c',
        borderRadius: '3px 0 0 3px',
        boxShadow: '-1px 0 3px rgba(0,0,0,0.4)'
      }} />
      <div style={{
        position: 'absolute',
        left: -5,
        top: 128,
        width: 4,
        height: 28,
        backgroundColor: '#4a3f5c',
        borderRadius: '3px 0 0 3px',
        boxShadow: '-1px 0 3px rgba(0,0,0,0.4)'
      }} />
      {/* ── Mute/silent toggle (left side, smaller) ── */}
      <div style={{
        position: 'absolute',
        left: -5,
        top: 60,
        width: 4,
        height: 20,
        backgroundColor: '#4a3f5c',
        borderRadius: '3px 0 0 3px',
        boxShadow: '-1px 0 3px rgba(0,0,0,0.4)'
      }} />
      {/* ── Power/lock button (right side) ── */}
      <div style={{
        position: 'absolute',
        right: -5,
        top: 110,
        width: 4,
        height: 44,
        backgroundColor: '#4a3f5c',
        borderRadius: '0 3px 3px 0',
        boxShadow: '1px 0 3px rgba(0,0,0,0.4)'
      }} />

      {/* ── Outer phone frame ── */}
      <div style={{
        width: 230,
        height: 460,
        borderRadius: 44,
        background: 'linear-gradient(145deg, #5c4f70 0%, #3d3349 40%, #2e2640 70%, #3a2f4a 100%)',
        padding: 3,
        boxShadow: '0 0 0 1px rgba(255,255,255,0.12), 0 28px 72px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.18)',
        position: 'relative'
      }}>
        {/* ── Inner bezel ── */}
        <div style={{
          width: '100%',
          height: '100%',
          borderRadius: 42,
          background: '#0d0d0d',
          padding: 6,
          overflow: 'hidden',
          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.06)'
        }}>
          {/* ── Screen area ── */}
          <div style={{
            width: '100%',
            height: '100%',
            borderRadius: 37,
            overflow: 'hidden',
            backgroundColor: '#000',
            position: 'relative'
          }}>
            {/* ── Dynamic Island pill notch ── */}
            <div style={{
              position: 'absolute',
              top: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 72,
              height: 22,
              backgroundColor: '#000',
              borderRadius: 12,
              zIndex: 20,
              boxShadow: '0 0 0 2px #0d0d0d'
            }} />

            {/* ── Screen content ── */}
            {heroVideo ?
            <>
                <video
                ref={videoRef}
                src={heroVideo.videoUrl}
                poster={heroVideo.thumbnailUrl || undefined}
                loop
                muted
                playsInline
                preload="metadata"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }} />

                <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  backgroundColor: isPlaying ? 'transparent' : 'rgba(0,0,0,0.25)'
                }}
                onClick={() => isPlaying ? onPause(heroVideo.id) : onPlay(heroVideo.id)}>

                  {!isPlaying &&
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>

                      <Play size={18} style={{ color: '#000', marginLeft: 3 }} fill="#000" />
                    </div>
                }
                </div>
              </> :

            <img
                src="/assets/images/0611-1781165608911.png"
                alt="Member dashboard preview"
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              />
            }
          </div>
        </div>
      </div>
    </div>);

}

// ─── FAQ accordion item ───────────────────────────────────────────────────────
function FaqItem({ q, a, defaultOpen }: {q: string;a: string;defaultOpen?: boolean;}) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div
      style={{
        backgroundColor: SURFACE,
        border: `1px solid ${BORDER}`,
        borderRadius: 8,
        overflow: 'hidden'
      }}>

      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between text-left py-[9px] px-[11px] lg:py-[14px] lg:px-[16px]"
        style={{ color: TEXT_PRIMARY, minHeight: 44, outline: 'none' }}
        aria-expanded={open}>

        <span style={{ color: TEXT_PRIMARY, fontSize: 14, fontWeight: 500, paddingRight: 16 }}>{q}</span>
        {open ?
        <ChevronUp size={16} style={{ color: TEXT_MUTED, flexShrink: 0 }} /> :

        <ChevronDown size={16} style={{ color: TEXT_MUTED, flexShrink: 0 }} />
        }
      </button>
      {open &&
      <div className="px-[11px] pb-[9px] lg:px-[16px] lg:pb-[14px]" style={{ marginTop: 8 }}>
          <p style={{ color: TEXT_BODY, fontSize: 14, lineHeight: 1.6, margin: 0 }}>{a}</p>
        </div>
      }
    </div>);

}

// ─── Main client component ────────────────────────────────────────────────────
export default function HomepageClient() {
  const [videos, setVideos] = useState<WinningVideo[]>([]);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const climbRef = useRef<HTMLDivElement>(null);
  const [featuredVideos, setFeaturedVideos] = useState<HomepageFeaturedVideo[]>([]);

  useEffect(() => {
    // Limit to first 4 videos only
    setVideos(getWinningVideos().slice(0, 4));
    const handler = () => setVideos(getWinningVideos().slice(0, 4));
    window.addEventListener('wb_winning_videos_changed', handler);
    return () => window.removeEventListener('wb_winning_videos_changed', handler);
  }, []);

  useEffect(() => {
    const loadFeaturedVideos = async () => {
      try {
        const supabase = createClient();
        const { data, error } = await supabase.
        from('homepage_videos').
        select('id, position, title, video_url, thumbnail_url, is_active').
        eq('is_active', true).
        order('position', { ascending: true });
        if (!error && data) {
          setFeaturedVideos(data as HomepageFeaturedVideo[]);
        }
      } catch {
        // silently fail — section still renders with placeholders
      }};loadFeaturedVideos();
  }, []);

  const handlePlay = useCallback((id: string) => setPlayingId(id), []);
  const handlePause = useCallback(
    (id: string) => setPlayingId((prev) => prev === id ? null : prev),
    []
  );

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_ITEMS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a }
    }))
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />


      <div style={{ backgroundColor: BG, color: TEXT_PRIMARY, fontFamily: 'Inter, system-ui, sans-serif', overflowX: 'hidden' }}>

        {/* ── FIX 2: Mobile centered logo (no top bar, no buttons) ─────────── */}
        <div
          className="flex lg:hidden"
          style={{ justifyContent: 'space-between', alignItems: 'center', paddingTop: 16, paddingBottom: 16, paddingLeft: 16, paddingRight: 16, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>

          <Link href="/" aria-label="WinBounty home">
            <LogoWithFallback height={100} />
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Link
              href="/sign-up-login-screen"
              style={{ color: '#9CA3AF', fontSize: 13, textDecoration: 'none' }}>

              Log in
            </Link>
            <Link
              href="/qualifying-application-form"
              style={{
                backgroundColor: EMERALD,
                color: EMERALD_TEXT,
                borderRadius: 8,
                padding: '5px 12px',
                fontSize: 14,
                fontWeight: 600,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                whiteSpace: 'nowrap'
              }}>

              Apply free
            </Link>
          </div>
        </div>

        {/* ── 1. STICKY NAV (desktop only) ──────────────────────────────────── */}
        <nav
          className="hidden lg:flex"
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 50,
            backgroundColor: 'rgba(10,14,12,0.9)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            borderBottom: DIVIDER,
            height: 72,
            alignItems: 'center',
            justifyContent: 'center',
            padding: '0 16px'
          }}>

          <div
            style={{
              maxWidth: 1120,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>

            <Link href="/" aria-label="WinBounty home">
              <LogoWithFallback height={140} />
            </Link>

            <div style={{ display: 'flex', alignItems: 'center', gap: 28 }}>
              {[
              { label: 'How it works', href: '#how-it-works' },
              { label: 'The climb', href: '#the-climb' },
              { label: 'Leaderboard', href: '#leaderboard' },
              { label: 'FAQ', href: '#faq' }].
              map(({ label, href }) =>
              <a
                key={href}
                href={href}
                style={{ color: TEXT_SECONDARY, fontSize: 14, textDecoration: 'none', transition: 'color 150ms' }}
                onMouseEnter={(e) => e.currentTarget.style.color = TEXT_PRIMARY}
                onMouseLeave={(e) => e.currentTarget.style.color = TEXT_SECONDARY}>

                  {label}
                </a>
              )}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Link
                href="/sign-up-login-screen"
                style={{
                  border: '1px solid rgba(255,255,255,0.14)',
                  color: TEXT_SECONDARY,
                  borderRadius: 8,
                  padding: '8px 16px',
                  fontSize: 14,
                  fontWeight: 500,
                  textDecoration: 'none',
                  minHeight: 44,
                  display: 'inline-flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap',
                  transition: 'color 150ms'
                }}>

                Log in
              </Link>
              <Link
                href="/qualifying-application-form"
                style={{
                  backgroundColor: EMERALD,
                  color: EMERALD_TEXT,
                  borderRadius: 8,
                  padding: '8px 16px',
                  fontSize: 14,
                  fontWeight: 600,
                  textDecoration: 'none',
                  minHeight: 44,
                  display: 'inline-flex',
                  alignItems: 'center',
                  whiteSpace: 'nowrap'
                }}>

                Apply free
              </Link>
            </div>
          </div>
        </nav>

        {/* ── 2. HERO ───────────────────────────────────────────────────────── */}
        <section
          style={{ borderBottom: DIVIDER, padding: '20px 16px' }}
          className="lg:px-10 lg:py-24">

          <div
            className="mx-auto flex flex-col lg:flex-row lg:items-center lg:gap-6"
            style={{ maxWidth: 1120 }}>

            <div className="flex-1 text-center lg:text-left">
              <p
                style={{
                  color: EMERALD,
                  fontSize: 14,
                  letterSpacing: '0.4px',
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  marginBottom: 12
                }}>

                FREE TO JOIN · PAID IN USD
              </p>
              <h1
                style={{
                  color: TEXT_PRIMARY,
                  fontSize: 'clamp(1.6rem, 5vw, 3.5rem)',
                  fontWeight: 700,
                  lineHeight: 1.15,
                  marginBottom: 16
                }} className="text-[36px] lg:text-4xl">

                Get paid in dollars<br className="hidden md:block" /> to make AI videos
              </h1>
              <p
                style={{
                  color: TEXT_SECONDARY,
                  fontSize: 'clamp(1rem, 2vw, 1.25rem)',
                  lineHeight: 1.7,
                  marginBottom: 24,
                  maxWidth: 560
                }}
                className="mx-auto lg:mx-0">

                Pick a bounty, create 3 short AI videos, get paid on approval. Bounties pay $20 to $100 each, rising as you level up. Get hired full-time as a bonus if you want it, or just keep earning on your own terms.
              </p>
              <div
                className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start"
                style={{ marginBottom: 16 }}>

                <Link
                  href="/qualifying-application-form"
                  style={{
                    backgroundColor: EMERALD,
                    color: EMERALD_TEXT,
                    borderRadius: 8,
                    padding: '12px 24px',
                    fontSize: 17,
                    fontWeight: 600,
                    textDecoration: 'none',
                    minHeight: 44,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>

                  Apply free, takes 3 minutes
                </Link>
                <a
                  href="#how-it-works"
                  style={{
                    border: '1px solid rgba(255,255,255,0.14)',
                    color: TEXT_SECONDARY,
                    borderRadius: 8,
                    padding: '12px 24px',
                    fontSize: 17,
                    fontWeight: 500,
                    textDecoration: 'none',
                    minHeight: 44,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>

                  See how it works
                </a>
              </div>
              <p style={{ color: TEXT_MUTED, fontSize: 14 }}>
                No fees, ever · Wise payouts on the 25th · No resume needed
              </p>
            </div>

            {/* Hero phone — desktop only. Plays first store video when available */}
            <div className="hidden lg:flex justify-center flex-shrink-0">
              <HeroVisual
                videos={videos}
                playingId={playingId}
                onPlay={handlePlay}
                onPause={handlePause} />

            </div>
          </div>
        </section>

        {/* ── 3. STAT BAR ───────────────────────────────────────────────────── */}
        <section style={{ borderTop: DIVIDER, borderBottom: DIVIDER }}>
          <div className="mx-auto" style={{ maxWidth: 1120 }}>
            <div
              className="grid grid-cols-3 md:grid-cols-4"
              style={{ borderColor: 'rgba(255,255,255,0.07)' }}>

              {[
              { value: '$20 to $100', label: 'per bounty', desktopOnly: false },
              { value: '$2,500+', label: 'by Legend', desktopOnly: false },
              { value: '25th', label: 'payday', desktopOnly: false },
              { value: 'Level up', label: 'to earn more', desktopOnly: true }].
              map(({ value, label, desktopOnly }, i) =>
              <div
                key={label}
                className={`${desktopOnly ? 'hidden md:flex' : 'flex'} flex-col items-center justify-center lg:py-4 lg:px-2`}
                style={{
                  borderLeft: i > 0 ? '1px solid rgba(255,255,255,0.07)' : 'none',
                  padding: '9px 4px'
                }}>

                  <span
                  className="tabular-nums"
                  style={{ color: EMERALD, fontSize: 'clamp(1.1rem, 3vw, 1.5rem)', fontWeight: 600 }}>

                    {value}
                  </span>
                  <span style={{ color: TEXT_MUTED, fontSize: 12, marginTop: 4 }}>{label}</span>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ── 4. HOW IT WORKS ───────────────────────────────────────────────── */}
        {/* Desktop: py-14 (56px). Mobile: py-5 (20px) px-4 (16px) */}
        <section
          id="how-it-works"
          style={{ borderBottom: DIVIDER, padding: '20px 16px' }}
          className="lg:px-10 lg:py-14">

          <div className="mx-auto" style={{ maxWidth: 1120 }}>
            <h2
              className="text-left lg:text-center"
              style={{
                color: TEXT_PRIMARY,
                fontSize: 'clamp(1.5rem, 4vw, 3rem)',
                fontWeight: 600,
                marginBottom: 12
              }}>

              From signup to payout in 4 steps
            </h2>

            {/* Mobile compact one-line rows */}
            <div className="lg:hidden flex flex-col" style={{ gap: 8 }}>
              {[
              { n: 1, text: 'Pass the quiz, 3 minutes, no resume' },
              { n: 2, text: 'Pick a bounty, $20 to $100 by level' },
              { n: 3, text: 'Create 3 AI videos, any tools you like' },
              { n: 4, text: 'Get approved, get paid via Wise' }].
              map(({ n, text }) =>
              <div
                key={n}
                style={{
                  backgroundColor: SURFACE,
                  borderRadius: 8,
                  padding: '9px 11px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>

                  <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: EMERALD,
                    color: EMERALD_TEXT,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 11,
                    fontWeight: 600,
                    flexShrink: 0
                  }}>

                    {n}
                  </div>
                  <span style={{ color: TEXT_BODY, fontSize: 11.5, lineHeight: 1 }}>{text}</span>
                </div>
              )}
            </div>

            {/* Desktop: four cards, 16px gap, 20px padding, 12px circle→title, 8px title→body */}
            <div className="hidden lg:grid grid-cols-4" style={{ gap: 16, marginTop: 28 }}>
              {[
              {
                n: 1,
                title: 'Pass the quiz',
                body: "3 minutes, multiple choice. No resume, no interview, no fees. Pass and you're in."
              },
              {
                n: 2,
                title: 'Pick a bounty',
                body: 'Real ad briefs from a real brand, with brand assets and examples included. Every bounty pays your level rate, from $20 up to $100. Hold up to 3 at once.'
              },
              {
                n: 3,
                title: 'Create 3 AI videos',
                body: 'Use whatever AI tools you want. Vertical 9:16, three variations per bounty.'
              },
              {
                n: 4,
                title: 'Get approved, get paid',
                body: 'Your reward hits your balance on approval. Wise sends your money on the 25th, every month.'
              }].
              map(({ n, title, body }) =>
              <div
                key={n}
                style={{
                  backgroundColor: SURFACE,
                  border: `1px solid ${BORDER}`,
                  borderRadius: 10,
                  padding: 20
                }}>

                  <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: EMERALD,
                    color: EMERALD_TEXT,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 14,
                    fontWeight: 700,
                    marginBottom: 12,
                    flexShrink: 0
                  }}>

                    {n}
                  </div>
                  <h3 style={{ color: TEXT_PRIMARY, fontWeight: 600, marginBottom: 8, fontSize: 15, marginTop: 0 }}>{title}</h3>
                  <p style={{ color: TEXT_BODY, fontSize: 14, lineHeight: 1.6, margin: 0 }}>{body}</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ── 5. THE CLIMB ──────────────────────────────────────────────────── */}
        {/* Desktop: py-14 (56px). Mobile: py-5 (20px) */}
        <section
          id="the-climb"
          ref={climbRef}
          style={{ borderBottom: DIVIDER, padding: '20px 16px' }}
          className="lg:px-10 lg:py-14">

          <div className="mx-auto" style={{ maxWidth: 1120 }}>
            <h2
              className="text-left lg:text-center mb-1 lg:mb-2"
              style={{
                color: TEXT_PRIMARY,
                fontSize: 'clamp(1.5rem, 4vw, 3rem)',
                fontWeight: 600
              }}>

              The more you win, the more you earn
            </h2>
            {/* h2 → subhead: 8px desktop, 4px mobile */}
            <p
              className="text-left lg:text-center"
              style={{ color: TEXT_SECONDARY, fontSize: 12, margin: 0 }}>

              Your pay per bounty rises every level. Totals are the minimum you'll have earned by each tier.
            </p>

            {/* Mobile ladder — subhead → ladder: 12px, row gap: 4px */}
            <div className="lg:hidden flex flex-col" style={{ gap: 4, marginTop: 12 }}>
              {TIER_LADDER.map((tier) =>
              <TierBarRow key={tier.name} tier={tier} sectionRef={climbRef} />
              )}
            </div>

            {/* Desktop ladder — subhead → ladder: 24px, row gap: 6px */}
            <div className="hidden lg:block" style={{ margin: '24px auto 0', maxWidth: 720 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {TIER_LADDER.map((tier) =>
                <TierBarRowDesktop key={tier.name} tier={tier} sectionRef={climbRef} />
                )}
              </div>
            </div>

            {/* ladder → caption: 12px */}
            <p
              className="text-left lg:text-center"
              style={{ color: TEXT_MUTED, fontSize: 10, marginTop: 12, marginBottom: 0 }}>

              Every bounty pays your current level rate. The more you win, the higher every bounty pays, for good.
            </p>

            {/* caption → callout: 12px, callout padding: 12px 16px */}
            <div
              style={{
                backgroundColor: CALLOUT_BG,
                border: `1px solid ${CALLOUT_BORDER}`,
                borderRadius: 8,
                padding: '12px 16px',
                marginTop: 12,
                maxWidth: 720,
                margin: '12px auto 0'
              }}>

              <p style={{ color: CALLOUT_TEXT, fontSize: 12, lineHeight: 1.6, margin: 0 }}>
                Start at $20 a bounty and climb to $100 at Legend. Reach Legend and you'll have earned $2,500 at minimum, with every bounty after paying $100.
              </p>
            </div>
          </div>
        </section>

        {/* ── 6. THE PRIZE ──────────────────────────────────────────────────── */}
        {/* Desktop: py-14 (56px). Mobile: py-5 (20px) */}
        <section
          style={{ borderBottom: DIVIDER, padding: '20px 16px' }}
          className="lg:px-10 lg:py-14">

          <div className="mx-auto flex flex-col items-center" style={{ maxWidth: 1120 }}>
            <h2
              className="text-left lg:text-center w-full mb-3 lg:mb-7"
              style={{
                color: TEXT_PRIMARY,
                fontSize: 'clamp(1.5rem, 4vw, 3rem)',
                fontWeight: 600
              }}>

              Get hired, if you want it
            </h2>
            {/* Card padding: 20px 24px desktop, 14px mobile */}
            <div
              style={{
                backgroundColor: SURFACE,
                border: `1px solid ${CALLOUT_BORDER}`,
                borderRadius: 10,
                maxWidth: 560,
                width: '100%'
              }}
              className="p-[14px] lg:p-[20px_24px]">

              {/* Full-time AI Prompter role */}
              <p style={{ color: TEXT_SECONDARY, fontSize: 12, margin: 0 }}>Full-time AI Prompter role</p>
              {/* → big line */}
              <p
                style={{
                  color: EMERALD,
                  fontSize: 'clamp(1.25rem, 4vw, 1.75rem)',
                  fontWeight: 700,
                  marginTop: 6,
                  marginBottom: 0
                }}>

                A bonus, not the goal
              </p>
              {/* → body line */}
              <p style={{ color: TEXT_BODY, fontSize: 12, lineHeight: 1.6, marginTop: 4, marginBottom: 0 }}>
                Top performers can get invited to a full-time role. Plenty of members never take it and just keep earning on bounties. Both are winning.
              </p>
              {/* → footer line */}
              <p style={{ color: TEXT_MUTED, fontSize: 11.5, marginTop: 10, marginBottom: 0 }}>
                On average, members who reach Level 5 get the invite. No connections, no resume games.
              </p>
            </div>
            {/* card → "Not looking for a job?" line: 16px */}
            <p
              className="text-left lg:text-center"
              style={{ color: TEXT_SECONDARY, fontSize: 13, maxWidth: 560, marginTop: 16, lineHeight: 1.6, width: '100%' }}>

              Not looking for a job? Keep winning bounties and getting paid, for as long as you want. The job is the prize, not the requirement.
            </p>
          </div>
        </section>

        {/* ── 6b. THIS IS WHAT WINNING LOOKS LIKE (featured videos) ─────────── */}
        {/* Desktop: py-14 (56px). Mobile: py-5 (20px) */}
        <section
          id="winning-looks-like"
          style={{ borderBottom: DIVIDER, padding: '20px 16px' }}
          className="lg:px-10 lg:py-14">

          <div className="mx-auto" style={{ maxWidth: 1120 }}>
            <h2
              className="text-left lg:text-center mb-1 lg:mb-2"
              style={{
                color: TEXT_PRIMARY,
                fontSize: 'clamp(1.5rem, 4vw, 3rem)',
                fontWeight: 700
              }}>

              This is what winning looks like
            </h2>
            {/* h2 → subhead: 4px mobile / 8px desktop. subhead → grid: 24px desktop, 12px mobile */}
            <p
              className="text-left lg:text-center"
              style={{ color: TEXT_SECONDARY, fontSize: 13, margin: 0 }}>

              Real submissions that got paid. Study them, then beat them.
            </p>
            {/* Mobile: 2 videos, gap 12px, subhead→grid 12px */}
            <div className="grid grid-cols-2 lg:hidden" style={{ gap: 12, maxWidth: 480, marginTop: 12 }}>
              {[0, 1].map((idx) => {
                const vid = featuredVideos.find((v) => v.position === idx + 1);
                return vid ?
                <HomepageFeaturedVideoCard key={vid.id} video={vid} /> :

                <HomepageFeaturedVideoPlaceholder key={`placeholder-${idx}`} />;

              })}
            </div>
            {/* Desktop: exactly 4 videos in 4-col grid, gap 12px, subhead→grid 24px */}
            <div className="hidden lg:grid lg:grid-cols-4" style={{ gap: 12, marginTop: 24 }}>
              {[0, 1, 2, 3].map((idx) => {
                const vid = featuredVideos.find((v) => v.position === idx + 1);
                return vid ?
                <HomepageFeaturedVideoCard key={vid.id} video={vid} /> :

                <HomepageFeaturedVideoPlaceholder key={`placeholder-${idx}`} />;

              })}
            </div>
          </div>
        </section>

        {/* ── 7. WINNING WORK ───────────────────────────────────────────────── */}
        {/* Desktop: py-14 (56px). Mobile: py-5 (20px) */}
        {videos.length > 0 &&
        <section
          id="winning"
          style={{ borderBottom: DIVIDER, padding: '20px 16px' }}
          className="lg:px-10 lg:py-14">

            <div className="mx-auto" style={{ maxWidth: 1120 }}>
              <h2
              className="text-left lg:text-center mb-1 lg:mb-2"
              style={{
                color: TEXT_PRIMARY,
                fontSize: 'clamp(1.5rem, 4vw, 3rem)',
                fontWeight: 600
              }}>

                This is what winning looks like
              </h2>
              {/* subhead → grid: 24px desktop, 12px mobile */}
              <p
              className="text-left lg:text-center"
              style={{ color: TEXT_SECONDARY, margin: 0, fontSize: 12 }}>

                Real submissions that got paid. Study them, then beat them.
              </p>
              {/* grid gap: 12px, subhead→grid: 12px mobile / 24px desktop */}
              <div className="grid grid-cols-2 lg:grid-cols-4 mt-3 lg:mt-6" style={{ gap: 12 }}>
                {videos.map((video) =>
              <VideoCard
                key={video.id}
                video={video}
                isPlaying={playingId === video.id}
                onPlay={handlePlay}
                onPause={handlePause} />

              )}
              </div>
            </div>
          </section>
        }

        {/* ── 8. LEADERBOARD ────────────────────────────────────────────────── */}
        {/* Desktop: py-14 (56px). Mobile: py-5 (20px) */}
        <section
          id="leaderboard"
          style={{ borderBottom: DIVIDER, padding: '20px 16px' }}
          className="lg:px-10 lg:py-14">

          <div className="mx-auto" style={{ maxWidth: 1120 }}>
            {/* h2 → rows: 24px desktop, 12px mobile */}
            <h2
              className="text-left lg:text-center mb-3 lg:mb-6"
              style={{
                color: TEXT_PRIMARY,
                fontSize: 'clamp(1.5rem, 4vw, 3rem)',
                fontWeight: 600
              }}>

              Real people, real payouts
            </h2>
            <div className="mx-auto" style={{ maxWidth: 640 }}>
              {/* Row gap: 6px mobile, 8px desktop */}
              <div className="flex flex-col gap-[6px] lg:gap-[8px]">
                {STATIC_LEADERBOARD.map((entry) =>
                <div
                  key={entry.rank}
                  style={{
                    backgroundColor: SURFACE,
                    borderRadius: 8,
                    padding: '8px 11px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}>

                    <span
                    style={{
                      color: TEXT_MUTED,
                      fontSize: 11,
                      width: 16,
                      textAlign: 'right',
                      flexShrink: 0,
                      fontVariantNumeric: 'tabular-nums'
                    }}>

                      {entry.rank}
                    </span>
                    <TierAvatar
                    level={entry.level}
                    tier={entry.tier}
                    isHired={false}
                    size="sm" />

                    <span
                    style={{
                      color: TEXT_PRIMARY,
                      fontSize: 11.5,
                      flex: 1,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>

                      {entry.username}
                    </span>
                    <span
                    style={{
                      color: EMERALD,
                      fontSize: 11.5,
                      fontWeight: 600,
                      flexShrink: 0,
                      fontVariantNumeric: 'tabular-nums'
                    }}>

                      {entry.earnings}
                    </span>
                  </div>
                )}
              </div>
              {/* rows → "Resets on the 1st..." line: 12px */}
              <p
                className="text-left lg:text-center"
                style={{ color: TEXT_MUTED, fontSize: 12, marginTop: 12, marginBottom: 0 }}>

                Resets on the 1st of every month. Everyone gets a fresh shot.
              </p>
            </div>
          </div>
        </section>

        {/* ── 9. LEGITIMACY ─────────────────────────────────────────────────── */}
        {/* Desktop: py-14 (56px). Mobile: py-5 (20px) */}
        <section
          id="legit"
          style={{ borderBottom: DIVIDER, padding: '20px 16px' }}
          className="lg:px-10 lg:py-14">

          <div className="mx-auto" style={{ maxWidth: 1120 }}>
            {/* h2 → body: 16px desktop, 10px mobile */}
            <h2
              className="text-left lg:text-center mb-[10px] lg:mb-4"
              style={{
                color: TEXT_PRIMARY,
                fontSize: 'clamp(1.5rem, 4vw, 3rem)',
                fontWeight: 600
              }}>

              Not like the other online jobs
            </h2>
            {/* body → chips: 20px desktop, 12px mobile */}
            <p
              className="text-left lg:text-center lg:mx-auto"
              style={{ color: TEXT_BODY, maxWidth: 640, lineHeight: 1.6, fontSize: 12, margin: 0 }}>

              You never pay us anything. No joining fees, no training packages, no unpaid trial tasks. We pay you, through Wise, on the 25th of every month. The rules are public: three videos per bounty, approved work gets paid, the leaderboard decides who gets interviewed.
            </p>
            {/* chip grid gap: 10px desktop, 7px mobile */}
            <div
              className="grid grid-cols-2 lg:grid-cols-4 lg:mx-auto mt-3 lg:mt-5 gap-[7px] lg:gap-[10px]"
              style={{ maxWidth: 720 }}>

              {['Free forever', 'Paid via Wise', 'Public rules', 'Hiring monthly'].map((label) =>
              <div
                key={label}
                style={{
                  backgroundColor: SURFACE,
                  border: `1px solid ${BORDER}`,
                  borderRadius: 8,
                  padding: '10px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8
                }}>

                  <div
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: EMERALD,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>

                    <Check size={11} style={{ color: EMERALD_TEXT }} strokeWidth={3} />
                  </div>
                  <span style={{ color: TEXT_PRIMARY, fontSize: 12, fontWeight: 500 }}>{label}</span>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ── 11. FAQ ───────────────────────────────────────────────────────── */}
        {/* Desktop: py-14 (56px). Mobile: py-5 (20px) */}
        <section
          id="faq"
          style={{ borderBottom: DIVIDER, padding: '20px 16px' }}
          className="lg:px-10 lg:py-14">

          <div className="mx-auto" style={{ maxWidth: 1120 }}>
            {/* h2 → accordion: 24px desktop, 12px mobile */}
            <h2
              className="text-left lg:text-center mb-3 lg:mb-6"
              style={{
                color: TEXT_PRIMARY,
                fontSize: 'clamp(1.5rem, 4vw, 3rem)',
                fontWeight: 600
              }}>

              Questions, answered straight
            </h2>
            {/* gap between items: 8px desktop, 6px mobile */}
            <div className="mx-auto flex flex-col gap-[6px] lg:gap-[8px]" style={{ maxWidth: 720 }}>
              {FAQ_ITEMS.map((item, i) =>
              <FaqItem key={item.q} q={item.q} a={item.a} defaultOpen={i === 0} />
              )}
            </div>
          </div>
        </section>

        {/* ── 12. FINAL CTA ─────────────────────────────────────────────────── */}
        {/* Desktop: py-[72px]. Mobile: py-6 (24px) */}
        <section
          style={{ borderBottom: DIVIDER, padding: '24px 16px', textAlign: 'center' }}
          className="lg:px-10 lg:py-[72px]">

          <div className="mx-auto" style={{ maxWidth: 640 }}>
            <h2
              style={{
                color: TEXT_PRIMARY,
                fontSize: 'clamp(1.5rem, 4vw, 3rem)',
                fontWeight: 600,
                marginBottom: 10
              }}>

              Every month, the top of the board gets the call
            </h2>
            {/* h2 → sub: 10px. sub → button: 24px */}
            <p style={{ color: TEXT_SECONDARY, marginBottom: 24, fontSize: 13, marginTop: 0 }}>
              The leaderboard resets on the 1st. Your first bounty is waiting.
            </p>
            <Link
              href="/qualifying-application-form"
              style={{
                backgroundColor: EMERALD,
                color: EMERALD_TEXT,
                borderRadius: 8,
                padding: '16px 32px',
                fontSize: 18,
                fontWeight: 600,
                textDecoration: 'none',
                minHeight: 56,
                display: 'inline-flex',
                alignItems: 'center'
              }}>

              Apply free now
            </Link>
            {/* button → tagline: 10px */}
            <p style={{ color: TEXT_MUTED, fontSize: 13, marginTop: 10, marginBottom: 0 }}>
              3-minute quiz · No fees · Paid in USD
            </p>
          </div>
        </section>

        {/* ── 13. FOOTER ────────────────────────────────────────────────────── */}
        {/* Desktop: py-7 (28px). Mobile: py-[14px] */}
        <footer
          style={{ borderTop: DIVIDER, padding: '14px 16px' }}
          className="lg:px-10 lg:py-7">

          <div
            className="mx-auto flex flex-col sm:flex-row items-center justify-between gap-6"
            style={{ maxWidth: 1120 }}>

            <Link href="/" aria-label="WinBounty home" style={{ textDecoration: 'none' }}>
              <LogoWithFallback height={80} />
            </Link>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 16 }}>
              {[
              { label: 'How it works', href: '#how-it-works' },
              { label: 'The climb', href: '#the-climb' },
              { label: 'FAQ', href: '#faq' },
              { label: 'Log in', href: '/sign-up-login-screen' },
              { label: 'Privacy', href: '/legal/privacy' },
              { label: 'Terms', href: '/legal/terms' }].
              map(({ label, href }) =>
              <a
                key={label}
                href={href}
                style={{ color: TEXT_MUTED, fontSize: 13, textDecoration: 'none', transition: 'color 150ms' }}
                onMouseEnter={(e) => e.currentTarget.style.color = TEXT_SECONDARY}
                onMouseLeave={(e) => e.currentTarget.style.color = TEXT_MUTED}>

                  {label}
                </a>
              )}
            </div>
            <p style={{ color: TEXT_MUTED, fontSize: 13 }}>Payouts via Wise · © WinBounty</p>
          </div>
        </footer>
      </div>
    </>);

}